import { createClient, LoginRequiredError } from "@workos-inc/authkit-js";
import { SignedOut, type Credential } from "./wire";

export type AuthKit = Awaited<ReturnType<typeof createClient>>;

// The one WorkOS redirect URI per environment, where the router serves the latest release's
// client page.
export const CALLBACK_PATH = "/callback";

// How this page's player proves who they are: an AuthKit session, or a user the server names
// itself (dev auth's ?user=).
export type Session =
  | { kind: "authkit"; authkit: AuthKit }
  | { kind: "named"; user: string };

// The access token stays in memory; the refresh token in localStorage (devMode) until the game
// goes public and a WorkOS custom auth domain moves it to an HttpOnly cookie. The strict
// CSP in index.html keeps every other script away from it.
export function startAuthKit(clientId: string, onReturn: (path: string) => void): Promise<AuthKit> {
  return createClient(clientId, {
    redirectUri: new URL(CALLBACK_PATH, location.origin).toString(),
    devMode: true,
    onRedirectCallback: ({ state }) => onReturn(returnPath(state?.["return_to"])),
  });
}

// The sign-in `state` carries where to come back to, such as /r/12/?game=abc.
export function signIn(authkit: AuthKit): Promise<void> {
  const here = location.pathname === CALLBACK_PATH ? document.baseURI : location.href;
  const url = new URL(here, location.origin);
  return authkit.signIn({ state: { return_to: url.pathname + url.search } });
}

// `state` is untrusted URL input, so only a path on this site is followed; anything else goes
// to the release the page came from.
function returnPath(wanted: unknown): string {
  if (typeof wanted === "string") {
    try {
      const url = new URL(wanted, location.origin);
      if (url.origin === location.origin && url.pathname !== CALLBACK_PATH) return url.href;
    } catch { /* not a URL: fall through */ }
  }
  return document.baseURI;
}

// A credential for the next request or socket. A signed-out AuthKit session throws SignedOut.
export async function freshCredential(session: Session, refresh = false): Promise<Credential> {
  if (session.kind === "named") return { kind: "dev-user", user: session.user };
  try {
    return { kind: "bearer", token: await session.authkit.getAccessToken({ forceRefresh: refresh }) };
  } catch (err) {
    if (err instanceof LoginRequiredError) throw new SignedOut(err.message);
    throw err;
  }
}

// Through WorkOS's logout, which ends the session there too, then back to this site's root.
// AuthKit throws when it holds no access token, so there's no session to end: just go home.
export function signOut(authkit: AuthKit): void {
  const home = new URL("/", location.origin).toString();
  try {
    authkit.signOut({ returnTo: home });
  } catch {
    location.assign(home);
  }
}
